import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  fillCourse,
  publishCollection,
  updateCollection,
} from '@platform/client';
import { courseFilledSchema } from '@platform/schema/authoring';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useTrackedMutation } from '../actions/ActionsProvider';
import {
  publishReadinessKey,
  publishReadinessQuery,
} from '../authoring/readiness';

import { collectionKey } from './collection';
import { counted } from './counted';
import { membershipChanged } from './members';
import { field, refusalGroups } from './refusals';

import type {
  CourseFilled,
  Published,
  ReadinessIssue,
} from '@platform/schema/authoring';

/**
 * Publishing a course or module — spec 013 §6.2. The one action with no machine on the
 * other end of it.
 *
 * The two kinds of objection are shown **separately**, because a reviewer has to know
 * which they are looking at. Validator issues are rules the content breaks and no
 * confirmation helps; reviewer blockers are an opinion, and overruling one takes a
 * second, explicit click — §8.3 keeps the reviewer advisory.
 *
 * A panel rather than a route since GEN-60 — `docs/decisions/library-screens.md`.
 */
export function PublishPanel({
  id,
  writing = false,
}: {
  id: string;
  /** An import is still writing lessons here, so the rules have nothing final to judge. */
  writing?: boolean;
}) {
  const queries = useQueryClient();
  const [acknowledged, setAcknowledged] = useState(false);
  // Keyed by the course, so confirming one never stands in for another.
  const [confirmedCourse, setConfirmedCourse] = useState<string>();

  /**
   * Everything a publish or a demote can change, in one place.
   *
   * The status is rendered by the header, the lessons by this panel and the row by the
   * list behind both. Naming them per mutation is how one of the three gets forgotten.
   */
  const refresh = async (): Promise<void> => {
    await Promise.all([
      // The same three a membership write makes stale, since publishing a subtree changes
      // what the members are and what the rules say about them — plus the status, which
      // only this panel moves. GEN-39 put the trio in one place after the two membership
      // writes were found listing a different set from this one.
      membershipChanged(queries, id),
      queries.invalidateQueries({ queryKey: collectionKey(id) }),
    ]);
  };

  // Asked again on every visit: a lesson or course the panel sent the admin to is fixed
  // on its own page, which invalidates nothing here.
  const readiness = useQuery({
    ...publishReadinessQuery(id),
    refetchOnMount: 'always',
  });

  // Readiness was judged against a module still being written; ask again once it is not.
  const wasWriting = useRef(writing);
  useEffect(() => {
    if (wasWriting.current && !writing)
      void queries.invalidateQueries({ queryKey: publishReadinessKey(id) });
    wasWriting.current = writing;
  }, [writing, queries, id]);

  const publish = useTrackedMutation('Publish', {
    mutationFn: () =>
      publishCollection({
        path: { id },
        body: { acknowledgeBlockers: acknowledged },
        throwOnError: true,
      }),
    // All of them, because the click changes all of them — GEN-24, widened by GEN-60.
    // The status now also reads from `collectionQuery`, through the header above this
    // panel: invalidating only what this file renders would leave the heading saying
    // DRAFT over a module that had just published, which is GEN-24 again one level up.
    onSuccess: refresh,
  });

  /**
   * The way back out. `placeLessons` refuses a published collection, so reordering one
   * failed with *"Move the collection back to DRAFT"* — an instruction the console
   * offered no way to follow, because publish was one-way here. Adding no longer needs
   * it; reordering and removing still do.
   *
   * The same `refresh` as publish since GEN-60. It used to invalidate readiness alone —
   * correct while this was its own route, where readiness was the only thing on screen
   * carrying a status. The header above it now carries one too, and a demote that left it
   * reading PUBLISHED is the bug this panel's own tests exist for.
   */
  const unpublish = useTrackedMutation('Move back to draft', {
    mutationFn: () =>
      updateCollection({
        path: { id },
        body: { status: 'DRAFT' },
        throwOnError: true,
      }),
    onSuccess: refresh,
  });

  /**
   * A curriculum build leaves its course a shell, and publishing a module publishes its
   * course — ADMIN-29. The fill writes what the shell is missing; the publish stays here.
   */
  const fill = useTrackedMutation('Fill in the course', {
    mutationFn: (course: string) =>
      fillCourse({ path: { id: course }, throwOnError: true }),
    onSuccess: refresh,
  });

  const state = readiness.data?.data;
  const issues = state?.issues ?? [];
  const blockers = state?.blockers ?? [];
  const stopped = state?.stopped ?? [];
  const draftLessons = state?.draftLessons ?? 0;
  const live = state?.status === 'PUBLISHED';
  const needsAcknowledgement =
    blockers.length + stopped.length > 0 && !acknowledged;
  const course = state?.course;
  const needsCourseConfirmation =
    course !== undefined && confirmedCourse !== course.id;
  const acknowledge = (
    <Button
      size="small"
      onClick={() => {
        setAcknowledged(true);
      }}
      disabled={acknowledged}
    >
      {acknowledged ? 'Acknowledged' : 'Acknowledge'}
    </Button>
  );
  const things = counted(issues.length, 'thing');
  const fillable = issues.find(
    ({ item, path }) => item.kind === 'COURSE' && FILLABLE.has(path),
  )?.item;

  if (writing)
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Button variant="contained" disabled>
          Publish everything under this collection
        </Button>
        <Typography variant="body2" color="text.secondary">
          Publish unlocks once the lessons are written
        </Typography>
      </Stack>
    );

  return (
    <Stack spacing={2} sx={{ maxWidth: 720 }}>
      {readiness.isSuccess &&
        issues.length + blockers.length + stopped.length === 0 && (
          <Alert severity="success">Nothing is blocking this.</Alert>
        )}

      {publish.isSuccess && (
        <Alert severity="success">{publishedSummary(publish.data.data)}</Alert>
      )}

      {course && (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              onClick={() => {
                setConfirmedCourse(course.id);
              }}
              disabled={!needsCourseConfirmation}
            >
              {needsCourseConfirmation ? 'Publish the course too' : 'Confirmed'}
            </Button>
          }
        >
          {`Publishing this also publishes its course, “${course.title}”, which is a draft. Learners will see the course on Explore.`}
        </Alert>
      )}

      {/* Rules can change after a publish, and a live module that misses a newer one
          stays live: the admin needs to know it is fine, and what blocks a republish.
          The count is of lines below, so it matches the list the admin works through. */}
      {fill.isSuccess && (
        <Alert severity="success">{filledSummary(fill.data.data)}</Alert>
      )}

      {issues.length > 0 && (
        <Alert
          severity={live ? 'info' : 'error'}
          action={
            fillable && (
              <Button
                size="small"
                disabled={fill.isPending}
                onClick={() => {
                  fill.mutate(fillable.id);
                }}
              >
                {fill.isPending ? 'Filling in…' : 'Fill in the course with AI'}
              </Button>
            )
          }
        >
          <Typography variant="body2">
            {live
              ? `Live. ${things} to fix before it can be republished.`
              : `${things} to fix before this can be published.`}
          </Typography>
          <Refusals issues={issues} id={id} />
        </Alert>
      )}

      {live && draftLessons > 0 && (
        <Alert severity="info">
          {draftLessons === 1
            ? 'Live. 1 lesson in it is a draft, so learners do not see it.'
            : `Live. ${String(draftLessons)} lessons in it are drafts, so learners do not see them.`}
        </Alert>
      )}

      {stopped.length > 0 && (
        <Alert severity="warning" action={acknowledge}>
          <Typography variant="body2">
            {`${counted(stopped.length, 'lesson')} came from a generation row that stopped part way, so what it wrote may be unfinished.`}
          </Typography>
          <Refusals issues={stopped} id={id} />
        </Alert>
      )}

      {blockers.length > 0 && (
        <Alert severity="warning" action={acknowledge}>
          <Typography variant="body2">
            {`The reviewer raised ${counted(blockers.length, 'blocker')} that ${blockers.length === 1 ? 'is' : 'are'} still open.`}
          </Typography>
          <Refusals issues={blockers} id={id} />
        </Alert>
      )}

      {/* Tied to the status rather than to `isSuccess`, which never resets: after moving
          back and publishing again the alert would otherwise still be here. */}
      {unpublish.isSuccess && state?.status === 'DRAFT' && (
        <Alert severity="info">
          Back to draft. An import can write to it again — lessons already
          published keep what they have. It is off Explore until you publish
          again.
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Button
          variant="contained"
          disabled={
            readiness.isPending ||
            issues.length > 0 ||
            needsAcknowledgement ||
            needsCourseConfirmation ||
            publish.isPending
          }
          onClick={() => {
            publish.mutate();
          }}
        >
          {/* Still offered once published: a draft lesson added later belongs to the same
              subtree, and this is what publishes it. */}
          {`Publish everything under this collection${live ? ' again' : ''}`}
        </Button>

        {issues.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {`Fix ${things} to ${live ? 'republish' : 'publish'}`}
          </Typography>
        )}

        {live && (
          <Button
            variant="outlined"
            disabled={unpublish.isPending}
            onClick={() => {
              unpublish.mutate();
            }}
          >
            Move back to draft
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

/** What a fill can write; a course refusal on any of them offers it. */
const FILLABLE = new Set<string>(
  courseFilledSchema.shape.filled.element.options,
);

function filledSummary({ filled }: CourseFilled) {
  return filled.length === 0
    ? 'The course needed nothing the fill can write.'
    : `Filled in the course’s ${filled.map((one) => field(one).label).join(', ')}.`;
}

function publishedSummary({ collections, lessons, course }: Published) {
  const including = course ? `, including the course “${course.title}”` : '';
  return `Published ${String(collections)} collection(s) and ${String(lessons)} lesson(s)${including}.`;
}

/** One group per card, linked to the page that fixes it — GEN-108. */
function Refusals({
  issues,
  id,
}: {
  issues: readonly ReadinessIssue[];
  id: string;
}) {
  return refusalGroups(issues, id).map((group) => (
    <Stack key={group.key} sx={{ mt: 1 }}>
      <Typography variant="subtitle2">
        {group.href === undefined ? (
          group.heading
        ) : (
          <Link component={RouterLink} to={group.href}>
            {group.heading}
          </Link>
        )}
      </Typography>
      <List dense disablePadding>
        {group.rules.map((rule, index) => (
          <ListItem key={index} disableGutters>
            <ListItemText
              primary={
                rule.where === undefined
                  ? rule.message
                  : `${rule.where}: ${rule.message}`
              }
              secondary={rule.field && <FieldLink {...rule.field} />}
            />
          </ListItem>
        ))}
      </List>
    </Stack>
  ));
}

/** Scrolled to and focused, so the admin lands in the field rather than near it. */
function FieldLink({ id, label }: { id: string; label: string }) {
  return (
    <Link
      href={`#${id}`}
      onClick={(event) => {
        event.preventDefault();
        const field = document.getElementById(id);
        field?.scrollIntoView({ block: 'center' });
        field?.focus();
      }}
    >
      Go to {label}
    </Link>
  );
}
